import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Field } from '../ui/Field';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { Checkbox } from '../ui/Checkbox';
import { useAssets } from '../../lib/api/assets';
import { useDuplicateCampaign } from '../../lib/api/campaigns';
import { useToast } from '../ui/Toast';
import { Campaign } from '../../lib/types';
import { fmtShort } from '../../lib/dates';

interface Props {
  open: boolean;
  onClose: () => void;
  source: Campaign;
  onCreated?: (newCampaignId: string) => void;
}

export const DuplicateCampaignModal = ({ open, onClose, source, onCreated }: Props) => {
  const toast = useToast();
  const duplicate = useDuplicateCampaign();
  const { data: assets = [], isLoading: loadingAssets } = useAssets({
    campaign: source.id,
  });

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [picked, setPicked] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!open) return;
    setName(`${source.name} (copy)`);
    setStartDate('');
    setEndDate('');
    setPicked(new Set(assets.map((a) => a.id)));
  }, [open, source.id, source.name, assets]);

  const sortedAssets = useMemo(
    () =>
      [...assets].sort((a, b) =>
        (a.publish_date ?? '').localeCompare(b.publish_date ?? ''),
      ),
    [assets],
  );

  const togglePicked = (id: number) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allChecked = sortedAssets.length > 0 && picked.size === sortedAssets.length;
  const noneChecked = picked.size === 0;
  const toggleAll = () => {
    if (allChecked) setPicked(new Set());
    else setPicked(new Set(sortedAssets.map((a) => a.id)));
  };

  const canSubmit =
    !!name.trim() && !!startDate && !!endDate && !duplicate.isPending;

  const submit = async () => {
    if (!canSubmit) return;
    if (endDate < startDate) {
      toast.push('End date must be on or after the start date.');
      return;
    }
    try {
      const created = await duplicate.mutateAsync({
        sourceId: source.id,
        payload: {
          name: name.trim(),
          start_date: startDate,
          end_date: endDate,
          asset_ids: Array.from(picked),
        },
      });
      toast.push(`Campaign "${created.name}" created`);
      onCreated?.(created.id);
      onClose();
    } catch (err) {
      console.error(err);
      toast.push('Could not duplicate campaign');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Duplicate — ${source.name}`}
      maxWidth={620}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {duplicate.isPending ? 'Duplicating…' : 'Create copy'}
          </Button>
        </>
      }
    >
      <Field label="Name" required>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Black Friday 2027"
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Start date" required>
          <input
            className="input"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Field>
        <Field label="End date" required>
          <input
            className="input"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Field>
      </div>

      <Field
        label={`Placements to copy (${picked.size}/${sortedAssets.length})`}
        hint="Tasks linked to a picked placement come with it. Campaign-only tasks come along too. All dates reset to the new start date so you can reschedule deliberately."
      >
        {loadingAssets ? (
          <div style={{ padding: 12, fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>
        ) : sortedAssets.length === 0 ? (
          <div style={{ padding: 12, fontSize: 12, color: 'var(--ink-4)' }}>
            This campaign has no assets to copy.
          </div>
        ) : (
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
              maxHeight: 280,
              overflowY: 'auto',
            }}
          >
            <button
              type="button"
              onClick={toggleAll}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: 'var(--bg-subtle)',
                borderBottom: '1px solid var(--border-soft)',
                fontSize: 11.5,
                color: 'var(--ink-3)',
                width: '100%',
                cursor: 'pointer',
              }}
            >
              <Checkbox
                checked={allChecked}
                onChange={toggleAll}
                onClick={(e) => e.stopPropagation()}
              />
              {allChecked ? 'Deselect all' : noneChecked ? 'Select all' : `Select all (${sortedAssets.length})`}
            </button>
            {sortedAssets.map((a) => (
              <label
                key={a.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '20px 1fr auto auto',
                  gap: 10,
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderBottom: '1px solid var(--border-soft)',
                  cursor: 'pointer',
                  fontSize: 12.5,
                }}
              >
                <Checkbox
                  checked={picked.has(a.id)}
                  onChange={() => togglePicked(a.id)}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'var(--ink-2)',
                    }}
                  >
                    {a.title || a.asset_type}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                    {a.category} · {a.asset_type}
                  </div>
                </div>
                <span className="mono" style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                  {a.publish_date ? fmtShort(a.publish_date) : 'To plan'}
                </span>
              </label>
            ))}
          </div>
        )}
      </Field>
    </Modal>
  );
};

export default DuplicateCampaignModal;
